import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import type { ProductCategory } from '@/api/admin-products-types'
import { useUpdateProductCategory } from '@/api/api-mutations'
import { useListProductCategories } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/product-categories/$categoryUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.ProductCategory.EditHeader()
	}
})

function RouteComponent() {
	const { categoryUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={api => api.getProductCategory({ path: { categoryUuid } })}>
			{data => <EditCategoryForm category={data} />}
		</RouteModal>
	)
}

function EditCategoryForm({ category }: { category: ProductCategory }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { categoryUuid } = Route.useParams()

	const updateCategory = useUpdateProductCategory()

	const [categorySearch, setCategorySearch] = useState('')
	const debouncedCategorySearch = useDebounce(categorySearch)
	const categories = useListProductCategories({
		q: debouncedCategorySearch,
		limit: 50
	})

	const form = useAppForm({
		defaultValues: {
			name: category.name,
			parentCategoryUuid: category.parentCategoryUuid ?? ''
		},
		onSubmit: async ({ value }) => {
			const result = await updateCategory({
				path: { categoryUuid },
				body: {
					name: value.name,
					parentCategoryUuid: value.parentCategoryUuid || undefined
				}
			})
			if (result.isSuccess) {
				toast.success(L.ProductCategory.EditSuccessToast({ name: value.name }))
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.ProductCategory.EditHeader()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
						<form.AppField
							name='parentCategoryUuid'
							children={field => (
								<field.SingleComboboxField
									label={L.ProductCategory.ParentCategory()}
									optional
									optionsLoading={categories.isLoading}
									onSearchValueChange={setCategorySearch}
									options={
										categories.data?.categories
											?.filter(x => x.uuid !== categoryUuid)
											?.map(c => ({ label: c.name, value: c.uuid })) ?? []
									}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
